// Collapsible syntax reference shown under the balance editor.

const HelpSection = () => (
  <div className="bg-gray-50 border border-gray-200 rounded px-4 py-3 mb-4 text-xs">
    <div className="mb-3 pb-3 border-b border-gray-200 text-gray-700">
      <span className="font-semibold">The anchor</span> is the first input
      you defined — the &ldquo;per-unit&rdquo; flow your balance scales
      against. Everything else (other inputs, outputs, electricity, water,
      emissions) is expressed as a function of the anchor. For a power
      plant the anchor is usually the fuel; for hydrogen production, you
      pick whichever side feels more natural to vary. The anchor&apos;s
      consumption is set automatically — you don&apos;t need to write{' '}
      <code>anchor = anchor</code>.
    </div>
    <div className="grid grid-cols-2 gap-x-6 gap-y-3">
      <div>
        <h5 className="font-semibold text-gray-700 mb-1">Writing the balance</h5>
        <ul className="space-y-1 text-gray-600">
          <li>
            <code>output = expr</code> — set an output flow.
            <div className="text-gray-500 ml-2">
              Readable on later lines once assigned. Each output can be
              written only once.
            </div>
          </li>
          <li>
            <code>input = expr</code> — declare a non-anchor input&apos;s
            consumption (electricity, water, oxygen, etc.).
          </li>
          <li>
            <code>name = expr</code> — intermediate variable. Use these to
            name a value you&apos;ll reuse below (e.g.{' '}
            <code>thermal_input = gas.energy_lhv</code>).
          </li>
          <li>
            <code>set output.&lt;property&gt; = expr</code> — override an
            output property (pressure, temperature, density, LHV, …).
            <div className="text-gray-500 ml-2">
              Use when the process changes a downstream property — e.g.
              compressing CO₂ to storage pressure. Outputs only. Place at
              the top of the balance.
            </div>
          </li>
          <li>
            <code>assert cond, &quot;msg&quot;</code> — sanity check
            evaluated at runtime.
          </li>
          <li><code># comment</code></li>
        </ul>
      </div>
      <div>
        <h5 className="font-semibold text-gray-700 mb-1">
          Reading species data
        </h5>
        <ul className="space-y-1 text-gray-600">
          <li>
            <code>&lt;io&gt;.lhv</code> · <code>.hhv</code> ·{' '}
            <code>.density</code> · <code>.carbon_ratio</code> ·{' '}
            <code>.molar_mass</code> · <code>.pressure</code> ·{' '}
            <code>.temperature</code>
            <div className="text-gray-500 ml-2">
              Live species properties on any input or output. Values propagate
              from upstream nodes if the user customized them there.
            </div>
          </li>
          <li className="pt-1">
            <span className="font-semibold text-gray-700">
              Flow conversions:
            </span>
            <div className="ml-2">
              <code>&lt;io&gt;.energy_lhv</code> · <code>.energy_hhv</code>{' '}
              · <code>.energy</code> · <code>.mass</code> ·{' '}
              <code>.volume</code> · <code>.moles</code>
            </div>
            <div className="text-gray-500 ml-2">
              Returns the IO&apos;s flow in the chosen basis. Auto-converts
              via species properties. Works on the anchor (always) or on
              any other IO once it&apos;s been assigned on an earlier line.
            </div>
          </li>
        </ul>
      </div>
      <div>
        <h5 className="font-semibold text-gray-700 mb-1">
          How outputs get their units
        </h5>
        <ul className="space-y-1 text-gray-600">
          <li>
            Each output has a canonical unit (kg for most species, kWh for
            electricity). The framework converts your assigned value to that
            unit automatically, using the species&apos;s heating value or
            density when needed.
          </li>
          <li className="text-gray-500">
            Inline annotations name the route: <code>(via lhv)</code>,{' '}
            <code>(via density)</code>, <code>(via molar_mass)</code>.
          </li>
          <li className="text-gray-500">
            Heating-value default is <strong>LHV</strong>. For HHV math, do
            it explicitly: <code>output.hhv / output.density</code>.
          </li>
        </ul>
      </div>
      <div>
        <h5 className="font-semibold text-gray-700 mb-1">Helpers</h5>
        <ul className="space-y-1 text-gray-600">
          <li>
            <code>close_carbon_balance()</code> — residual CO₂ (or other
            carbon-bearing species).
            <div className="text-gray-500 ml-2">
              Use as the last carbon-bearing output line. Sums carbon
              entering (anchor + inputs) minus carbon already assigned to
              outputs above, then converts to the LHS species&apos;s mass.
            </div>
          </li>
          <li className="pt-1">
            <span className="font-semibold text-gray-700">Math:</span>{' '}
            <code>log</code> · <code>log10</code> · <code>exp</code> ·{' '}
            <code>sqrt</code> · <code>min</code> · <code>max</code> ·{' '}
            <code>abs</code> · <code>**</code> (power)
          </li>
          <li>
            <span className="font-semibold text-gray-700">Constants:</span>{' '}
            <code>pi</code> · <code>e</code> · <code>hours_per_year</code> ·{' '}
            <code>True</code> · <code>False</code>
          </li>
        </ul>
      </div>
    </div>
  </div>
);

export default HelpSection;
